// frontend/src/hooks/useComments.ts
import { useState, useEffect, useCallback, useRef } from 'react';
import { dbInsert, dbUpdate, dbDelete, dbRpc } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';

// Matches the Replyke comment data model shape
export interface Comment {
  id: string;
  project_public_id: string;
  user_id: string;
  parent_id: string | null;
  content: string;
  mentions: { id: string; username: string }[];
  upvotes: string[];
  downvotes: string[];
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
  // Joined from profiles
  author_username: string | null;
  author_display_name: string | null;
  author_avatar_url: string | null;
}

export interface MentionUser {
  id: string;
  username: string;
  display_name: string | null;
}

// Tree node for threaded display
export interface CommentNode extends Comment {
  replies: CommentNode[];
  repliesCount: number;
}

function buildTree(comments: Comment[]): CommentNode[] {
  const map = new Map<string, CommentNode>();
  const roots: CommentNode[] = [];

  // Create nodes
  for (const c of comments) {
    map.set(c.id, { ...c, replies: [], repliesCount: 0 });
  }

  // Link children to parents
  for (const node of map.values()) {
    if (node.parent_id && map.has(node.parent_id)) {
      const parent = map.get(node.parent_id)!;
      parent.replies.push(node);
      parent.repliesCount++;
    } else {
      roots.push(node);
    }
  }

  // Sort replies by created_at ascending
  for (const node of map.values()) {
    node.replies.sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
  }

  // Prune soft-deleted nodes that have no visible replies.
  // A deleted comment is only kept if it has at least one non-deleted descendant
  // (to preserve thread context). Otherwise it disappears entirely.
  function prune(nodes: CommentNode[]): CommentNode[] {
    return nodes.reduce<CommentNode[]>((acc, node) => {
      node.replies = prune(node.replies);
      node.repliesCount = node.replies.length;
      // Drop deleted nodes whose entire subtree is empty
      if (node.deleted_at && node.replies.length === 0) {
        return acc;
      }
      acc.push(node);
      return acc;
    }, []);
  }

  return prune(roots);
}

export function useComments(projectPublicId: string | undefined) {
  const { session, user, profile } = useAuth();
  const [comments, setComments] = useState<Comment[]>([]);
  const [commentTree, setCommentTree] = useState<CommentNode[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sortBy, setSortBy] = useState<'newest' | 'oldest' | 'popular'>('newest');
  const fetchedRef = useRef(false);

  const token = session?.access_token;

  const fetchComments = useCallback(async () => {
    if (!projectPublicId) return;

    setLoading(true);
    setError(null);

    const { data, error: fetchError } = await dbRpc<Comment[]>(
      'get_project_comments',
      { p_project_public_id: projectPublicId },
      { token }
    );

    if (fetchError) {
      setError(fetchError.message);
      setLoading(false);
      return;
    }

    const fetched = data || [];
    setComments(fetched);
    setCommentTree(buildTree(fetched));
    setLoading(false);
  }, [projectPublicId, token]);

  useEffect(() => {
    if (projectPublicId && !fetchedRef.current) {
      fetchedRef.current = true;
      fetchComments();
    }
  }, [projectPublicId, fetchComments]);

  // Reset when projectPublicId changes
  useEffect(() => {
    fetchedRef.current = false;
  }, [projectPublicId]);

  // Sort the tree
  const sortedTree = useCallback(() => {
    const sorted = [...commentTree];
    if (sortBy === 'newest') {
      sorted.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
    } else if (sortBy === 'oldest') {
      sorted.sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
    } else if (sortBy === 'popular') {
      sorted.sort((a, b) => {
        const scoreA = a.upvotes.length - a.downvotes.length;
        const scoreB = b.upvotes.length - b.downvotes.length;
        return scoreB - scoreA;
      });
    }
    return sorted;
  }, [commentTree, sortBy]);

  const addComment = useCallback(async (
    content: string,
    mentions: { id: string; username: string }[],
    parentId: string | null = null
  ): Promise<{ error: string | null }> => {
    if (!user || !token || !projectPublicId) {
      return { error: 'You must be signed in to comment' };
    }

    const { data, error: insertError } = await dbInsert<Comment>(
      'comments',
      {
        project_public_id: projectPublicId,
        user_id: user.id,
        parent_id: parentId,
        content: content.trim(),
        mentions: JSON.stringify(mentions),
      },
      { token }
    );

    if (insertError) {
      return { error: insertError.message };
    }

    if (data) {
      // Enrich with current user's profile info
      const enriched: Comment = {
        ...data,
        mentions: typeof data.mentions === 'string' ? JSON.parse(data.mentions as unknown as string) : data.mentions,
        author_username: profile?.username || null,
        author_display_name: profile?.display_name || null,
        author_avatar_url: profile?.avatar_url || null,
      };

      setComments(prev => {
        const updated = [...prev, enriched];
        setCommentTree(buildTree(updated));
        return updated;
      });
    }

    return { error: null };
  }, [user, token, projectPublicId, profile]);

  const updateComment = useCallback(async (
    commentId: string,
    content: string,
    mentions: { id: string; username: string }[]
  ): Promise<{ error: string | null }> => {
    if (!user || !token) {
      return { error: 'You must be signed in' };
    }

    const { error: updateError } = await dbUpdate(
      'comments',
      {
        content: content.trim(),
        mentions: JSON.stringify(mentions),
        updated_at: new Date().toISOString(),
      },
      { id: `eq.${commentId}`, user_id: `eq.${user.id}` },
      { token }
    );

    if (updateError) {
      return { error: updateError.message };
    }

    setComments(prev => {
      const updated = prev.map(c =>
        c.id === commentId
          ? { ...c, content: content.trim(), mentions, updated_at: new Date().toISOString() }
          : c
      );
      setCommentTree(buildTree(updated));
      return updated;
    });

    return { error: null };
  }, [user, token]);

  const deleteComment = useCallback(async (
    commentId: string
  ): Promise<{ error: string | null }> => {
    if (!user || !token) {
      return { error: 'You must be signed in' };
    }

    // Check if comment has replies from other users
    const hasRepliesFromOthers = comments.some(
      c => c.parent_id === commentId && c.user_id !== user.id && !c.deleted_at
    );

    if (hasRepliesFromOthers) {
      // Soft delete - keep as [deleted] so the thread context is preserved
      const { error: updateError } = await dbUpdate(
        'comments',
        {
          deleted_at: new Date().toISOString(),
          content: '[deleted]',
        },
        { id: `eq.${commentId}`, user_id: `eq.${user.id}` },
        { token }
      );

      if (updateError) {
        return { error: updateError.message };
      }

      setComments(prev => {
        const updated = prev.map(c =>
          c.id === commentId
            ? { ...c, deleted_at: new Date().toISOString(), content: '[deleted]' }
            : c
        );
        setCommentTree(buildTree(updated));
        return updated;
      });
    } else {
      // Hard delete - remove entirely (CASCADE will remove own child comments too)
      const { error: deleteError } = await dbDelete(
        'comments',
        { id: `eq.${commentId}`, user_id: `eq.${user.id}` },
        { token }
      );

      if (deleteError) {
        return { error: deleteError.message };
      }

      setComments(prev => {
        // Remove this comment and any of its descendants
        const idsToRemove = new Set<string>();
        idsToRemove.add(commentId);
        // Collect all descendant IDs (children, grandchildren, etc.)
        let changed = true;
        while (changed) {
          changed = false;
          for (const c of prev) {
            if (c.parent_id && idsToRemove.has(c.parent_id) && !idsToRemove.has(c.id)) {
              idsToRemove.add(c.id);
              changed = true;
            }
          }
        }
        const updated = prev.filter(c => !idsToRemove.has(c.id));
        setCommentTree(buildTree(updated));
        return updated;
      });
    }

    return { error: null };
  }, [user, token, comments]);

  const toggleUpvote = useCallback(async (
    commentId: string
  ): Promise<{ error: string | null }> => {
    if (!user || !token) {
      return { error: 'You must be signed in to vote' };
    }

    // Optimistic update
    setComments(prev => {
      const updated = prev.map(c => {
        if (c.id !== commentId) return c;
        const hasUpvoted = c.upvotes.includes(user.id);
        return {
          ...c,
          upvotes: hasUpvoted
            ? c.upvotes.filter(id => id !== user.id)
            : [...c.upvotes, user.id],
          downvotes: c.downvotes.filter(id => id !== user.id),
        };
      });
      setCommentTree(buildTree(updated));
      return updated;
    });

    const { error: rpcError } = await dbRpc(
      'toggle_comment_upvote',
      { comment_id: commentId },
      { token }
    );

    if (rpcError) {
      // Revert on error
      await fetchComments();
      return { error: rpcError.message };
    }

    return { error: null };
  }, [user, token, fetchComments]);

  const toggleDownvote = useCallback(async (
    commentId: string
  ): Promise<{ error: string | null }> => {
    if (!user || !token) {
      return { error: 'You must be signed in to vote' };
    }

    // Optimistic update
    setComments(prev => {
      const updated = prev.map(c => {
        if (c.id !== commentId) return c;
        const hasDownvoted = c.downvotes.includes(user.id);
        return {
          ...c,
          downvotes: hasDownvoted
            ? c.downvotes.filter(id => id !== user.id)
            : [...c.downvotes, user.id],
          upvotes: c.upvotes.filter(id => id !== user.id),
        };
      });
      setCommentTree(buildTree(updated));
      return updated;
    });

    const { error: rpcError } = await dbRpc(
      'toggle_comment_downvote',
      { comment_id: commentId },
      { token }
    );

    if (rpcError) {
      await fetchComments();
      return { error: rpcError.message };
    }

    return { error: null };
  }, [user, token, fetchComments]);

  const searchMentionUsers = useCallback(async (
    searchTerm: string
  ): Promise<MentionUser[]> => {
    if (!searchTerm || searchTerm.length < 1) return [];

    const { data } = await dbRpc<MentionUser[]>(
      'search_users_for_mention',
      { search_term: searchTerm, result_limit: 5 },
      { token }
    );

    return data || [];
  }, [token]);

  return {
    comments,
    commentTree: sortedTree(),
    loading,
    error,
    sortBy,
    setSortBy,
    addComment,
    updateComment,
    deleteComment,
    toggleUpvote,
    toggleDownvote,
    searchMentionUsers,
    refreshComments: fetchComments,
    totalCount: comments.length,
  };
}